import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { logger } from "src/lib/utils";
import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import client from "../../api";
import { Table, Form, Input, Modal } from "antd";
import moment from "moment";
import routes from "src/config/routes";
import { permissions as permissionConstants } from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

interface ICardProduct {
  id: number;
  key: number;
  code: string;
  name: string;
  bank_type_id: number;
  benefits: string[];
  cash_out_limit: string;
  internal_transfers: string;
  card_type_duration: string;
  max_cards_per_product: number;
  active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ICardProductResponse {
  status: string;
  code: number;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    data: ICardProduct[];
  };
}

interface IListParams {
  page: number;
  limit: number;
  search?: string;
}

const CardProducts = () => {
  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.CARD_PRODUCTS
  );
  const canViewProducts = canView || canList;

  const [dataSource, setDataSource] = useState<ICardProduct[]>([]);
  const [editingItem, setEditingItem] = useState<ICardProduct | null>(null);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [total, setTotal] = useState(0);
  // LIST API
  const { mutate: getProductList, isLoading } = useMutation<
    ICardProductResponse,
    Error,
    IListParams
  >(client.cardProducts?.list, {
    onSuccess: (res) => {
      logger.log("list response", res);

      setDataSource(
        (res.data.data ?? []).map((item) => ({
          ...item,
          key: item.id,
        })),
      );

      setTotal(res.data.total);
    },
  });

  // DELETE
  const { mutate: deleteProduct } = useMutation(client.cardProducts?.delete, {
    onSuccess: (_, variables) => {
      getProductList({ page, limit });
    },
  });

  const onDelete = (id: string) => {
    if (!canDelete) return;
    Modal.confirm({
      title: "Delete Package",
      content: "Are you sure you want to delete this product?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        deleteProduct({ id }); // API call
      },
    });
  };

  useEffect(() => {
    if (!canViewProducts) return;
    getProductList({
      page,
      limit,
      search: debouncedSearch || undefined,
      // active,
    });
  }, [canViewProducts, page, limit, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1); // reset page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
    },

    {
      title: "Code",
      dataIndex: "code",
      width: 120,
    },

    {
      title: "Name",
      dataIndex: "name",
      width: 180,
    },

    {
      title: "Bank Type",
      dataIndex: "bank_type_id",
      width: 120,
    },

    {
      title: "Cash Out Limit",
      dataIndex: "cash_out_limit",
      width: 130,
      render: (value: string) => `${value}`,
    },

    {
      title: "Internal Transfers",
      dataIndex: "internal_transfers",
      width: 150,
      render: (value: string) => `${value}`,
    },

    {
      title: "Duration (Months)",
      dataIndex: "card_type_duration",
      width: 150,
      render: (value: string) => `${value} Months`,
    },

    {
      title: "Max Cards",
      dataIndex: "max_cards_per_product",
      width: 120,
    },

    {
      title: "Benefits",
      dataIndex: "benefits",
      width: 260,
      render: (benefits: string[]) => (
        <ul className="list-disc pl-5 mb-0">
          {benefits.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ),
    },

    {
      title: "Display Order",
      dataIndex: "display_order",
      width: 120,
    },

    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      render: (active: boolean) => (
        <span
          className={
            active ? "text-green-600 font-medium" : "text-red-500 font-medium"
          }
        >
          {active ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      title: "Created",
      dataIndex: "created_at",
      width: 170,
      render: (date: string) => moment(date).format("DD MMM YYYY HH:mm"),
    },

    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: ICardProduct) => {
        if (!canEdit && !canDelete) {
          return "—";
        }
        return (
          <div className="flex gap-2">
            {canDelete && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => onDelete(record.id.toString())}
              >
                <DeleteIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}

            {canEdit && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => navigate(`/card-products/edit/${record.id}`)}
              >
                <EditIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-semibold">Card Products</h2>

        {/* <Input.Search
          allowClear
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
        /> */}

        <PermissionGate
          module={[permissionConstants.CARD_PRODUCTS]}
          action="can_add"
        >
          <button
            className="flex items-center gap-2 px-4 py-2 bg-white rounded hover:bg-[#E6E8EC]"
            onClick={() => navigate(routes.cardProductForm)}
          >
            <p className="font-manrope text-[14px] font-medium text-primary">
              Add Product
            </p>
            <PlusIcon className="w-[18px] h-[18px] text-primary" />
          </button>
        </PermissionGate>
      </div>
      {!canViewProducts ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view card products.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table<ICardProduct>
            rowKey="id"
            columns={columns}
            dataSource={dataSource}
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              position: ["bottomCenter"],
              onChange: (newPage, newLimit) => {
                setPage(newPage);
                setLimit(newLimit);
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CardProducts;
